import React from "react";
import { useState, useEffect } from "react";
import Stepper from "react-stepper-horizontal";
import ImageBanner from "../../components/ImageBanner";
import {
  Button,
  Dropdown,
  Form,
  Input,
  Loader,
  Radio,
  RadioGroup,
} from "rsuite";
import CustomInput from "../../components/CustomInput";
import { useParams, useSearchParams } from "react-router-dom";
import CustomDropdown from "../../components/CustomDropdown";
import { PrimaryTraveler } from "./components/PrimaryTraveler";
import { SecondaryTraveler } from "./components/SecondaryTraveler";
import { Accommodations } from "./components/Accommodations";
import { TravelInsurance } from "./components/TravelInsurance";
import { DepositSubmit } from "./components/DepositSubmit";
import {
  BookNowValidationInReservation, validateAccomodationData,
  validatePrimaryTravelerData, validateSecondaryTravelerData, validateTravelInsurenceData,
} from "../../validations";
import apiRoutes from "../../api/apiRoutes";
import ApiCall from "../../api/network";
import toast from "react-hot-toast";
import {useFetchUserProfile} from "../../hooks/useQuery/useUserProfile";
// import "./ReservationForm.css"; // Import a separate CSS file for styling

const ReservationForm = () => {
  const [loading, setLoading] = useState(false);
  // const tour_booking_id = false;
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [booked, setBooked] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [passengerNo, setPassengerNo] = useState(searchParams.get("number_of_passengers") || 1 );
  const [sharing, setSharing] = useState(0);

  //user
  const {
    isLoading,
    isError,
    error,
    data: {
      data: user
    } = {},
    refetch,
  } = useFetchUserProfile();

  // Step 1: Primary Traveler
  const booking_number = searchParams.get("booking_number");
  const name_of_trip = searchParams.get("name_of_trip");
  const tour_id = searchParams.get("tour_id");
  const tour_booking_id = searchParams.get("tour_booking_id");
  const tour_supplier = searchParams.get("supplier");
  const isCustomForm = !id;

  const [primaryTravelerData, setPrimaryTravelerData] = useState({
    name_of_trip: name_of_trip,
    supplier_name: tour_supplier || "",
    booking_number: booking_number ? booking_number : "",
    tour_id: tour_id,
    tour_uuid: id,
    full_name: "",
    companion: 'no',
    passport_number: "",
    passport_expiry_date: "",
    passport_issue_country: "",
    nationality: "",
    date_of_birth: "",
    full_street_address: "",
    city: "",
    country: "",
    province_or_state: "",
    postal_code_or_zip_code: "",
    email: "",
    phone_number: "",
    emergency_contact_name: "",
    emergency_contact_number: "",
    emergency_contact_email: "",
    food_allergies: null,
    // special_meal_requirements: "No",
    mobility_issues: null,
  });

  // Step 2: Secondary Traveler
  const [secondaryTravelerData, setSecondaryTravelerData] = useState({
    secondary_full_name: "",
    secondary_passport_number: "",
    secondary_passport_expiry_date: "",
    secondary_passport_issue_country: "",
    secondary_nationality: "",
    secondary_date_of_birth: "",
    secondary_full_street_address: "",
    secondary_city: "",
    secondary_country: "",
    secondary_province_or_state: "",
    secondary_postal_code_or_zip_code: "",
    secondary_email: "",
    secondary_phone_number: "",
    secondary_emergency_contact_name: "",
    secondary_emergency_contact_number: "",
    secondary_emergency_contact_email: "",
    secondary_food_allergies: null,
    // secondary_special_meal_requirements: "No",
    secondary_mobility_issues: null,
  });

  // ✅ Update full_name once user is fetched
  useEffect(() => {
    if (!isLoading && !isError) {
      setPrimaryTravelerData(prev => ({
        ...prev,
        contact: user?.contact,
        full_name: user?.passport_name ? user?.passport_name : `${user?.first_name || ""} ${user?.last_name || ""}`,
        passport_number: user?.passport_number,
        passport_expiry_date: user?.passport_expiry,
        passport_issue_country: user?.passport_issue_country,
        nationality: user?.nationality,
        date_of_birth: user?.dob,
        full_street_address: user?.street_address,
        city: user?.city,
        home_departure_city: user?.preferred_home_airport,
        country: user?.country,
        province_or_state: user?.province,
        postal_code_or_zip_code: user?.postal_code,
        email: user?.email,
        phone_number: user?.contact,
        emergency_contact_name: user?.emergency_contact_name,
        emergency_contact_number: user?.emergency_contact_number,
        emergency_contact_email: user?.emergency_contact_email,
        food_allergies: user?.food_allergies,
        mobility_issues: user?.mobility_issues,
      }));
      setSecondaryTravelerData(prev => ({
        ...prev,
        secondary_full_name: user?.secondary_traveller?.full_name,
        secondary_passport_number: user?.secondary_traveller?.passport_number,
        secondary_passport_expiry_date: user?.secondary_traveller?.passport_expiry,
        secondary_passport_issue_country: user?.secondary_traveller?.passport_issue_country,
        secondary_nationality: user?.secondary_traveller?.nationality,
        secondary_date_of_birth: user?.secondary_traveller?.dob,
        secondary_full_street_address: user?.secondary_traveller?.street_address,
        secondary_city: user?.secondary_traveller?.city,
        secondary_country: user?.secondary_traveller?.country,
        secondary_province_or_state: user?.secondary_traveller?.state,
        secondary_postal_code_or_zip_code: user?.secondary_traveller?.postal_code,
        secondary_email: user?.secondary_traveller?.email,
        secondary_phone_number: user?.secondary_traveller?.contact,
        secondary_emergency_contact_name: user?.secondary_traveller?.emergency_contact_name,
        secondary_emergency_contact_number: user?.secondary_traveller?.emergency_contact_number,
        secondary_emergency_contact_email: user?.secondary_traveller?.emergency_contact_email,
        secondary_food_allergies: user?.secondary_traveller?.food_allergies,
        // secondary_special_meal_requirements: "No",
        secondary_mobility_issues: user?.secondary_traveller?.mobility_issues,
      }));
    }
  }, [user]);

  // Step 3: Accommodations
  const [accommodationsData, setAccommodationsData] = useState({
    // beds: 1, // 1 bed / 2 bed
    beds: null, // 1 bed / 2 bed
    sharing_accommodation: null, // 1 / 0
  });

  // Step 4: Travel Insurance
  const [travelInsuranceData, setTravelInsuranceData] = useState({
    travel_insurance: null,
  });

  // Step 5: Deposit & Submit
  const [depositSubmitData, setDepositSubmitData] = useState({});

  // Function to handle form data change
  const handleFormDataChange = (step, field, value) => {
    switch (step) {
      case 0:
        setPrimaryTravelerData((prevData) => ({ ...prevData, [field]: value }));
        if (Object.keys(errors).length > 0) {
          if (errors.hasOwnProperty(field)) delete errors[field];
        }
        break;
      case 1:
        setSecondaryTravelerData((prevData) => ({
          ...prevData,
          [field]: value,
        }));
        if (Object.keys(errors).length > 0) {
          if (errors.hasOwnProperty(field)) delete errors[field];
        }
        break;
      case 2:
        setAccommodationsData((prevData) => ({ ...prevData, [field]: value }));
        if (Object.keys(errors).length > 0) {
          if (errors.hasOwnProperty(field)) delete errors[field];
        }
        break;
      case 3:
        setTravelInsuranceData((prevData) => ({ ...prevData, [field]: value }));
        if (Object.keys(errors).length > 0) {
          if (errors.hasOwnProperty(field)) delete errors[field];
        }
        break;
      case 4:
        setDepositSubmitData((prevData) => ({ ...prevData, [field]: value }));

        break;
      default:
        break;
    }
  };

  const steps = [
    { title: "Primary Traveler", onClick: () => setActiveStep(0) },
    ...(passengerNo > 1 ? [{ title: "Secondary Traveler", onClick: () => setActiveStep(1) }] : []),
    { title: "Accommodations", onClick: () => setActiveStep(2) },
    { title: "Travel Insurance", onClick: () => setActiveStep(3) },
    { title: "Submit", onClick: () => setActiveStep(4) },
  ];
  const allData = [
    { title: "Primary Traveler's Information", data: primaryTravelerData },
    ...(passengerNo > 1 ? [{ title: "Secondary Traveler's Information", data: secondaryTravelerData }] : []),
    { title: "Accommodation Details", data: accommodationsData },
    { title: "Travel Insurance Details", data: travelInsuranceData },
  ];

  const getSectionComponent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PrimaryTraveler
            data={primaryTravelerData}
            onChange={(field, value) => handleFormDataChange(0, field, value)}
            errors={errors}
            tour_booking_id={tour_booking_id ? tour_booking_id : undefined}
            setPassengerNo={setPassengerNo}
          />
        );
      case 1:
        return (
            passengerNo > 1 ?
          <SecondaryTraveler
            data={secondaryTravelerData}
            onChange={(field, value) => handleFormDataChange(1, field, value)}
            errors={errors}
          />
                :
                <Accommodations
                    data={accommodationsData}
                    onChange={(field, value) => handleFormDataChange(2, field, value)}
                    errors={errors}
                    passengerNo={passengerNo}
                    sharing={sharing}
                    setSharing={setSharing}
                />
        );
      case 2:
        return (
            passengerNo > 1 ?
          <Accommodations
            data={accommodationsData}
            onChange={(field, value) => handleFormDataChange(2, field, value)}
            errors={errors}
            passengerNo={passengerNo}
            sharing={sharing}
            setSharing={setSharing}
          />:
                <TravelInsurance
                    data={travelInsuranceData}
                    onChange={(field, value) => handleFormDataChange(3, field, value)}
                    errors={errors}
                />
        );
      case 3:
        return (
            passengerNo > 1 ?
          <TravelInsurance
            data={travelInsuranceData}
            onChange={(field, value) => handleFormDataChange(3, field, value)}
            errors={errors}

          />:
                <DepositSubmit
                    data={allData}
                    booked={booked}
                    // onChange={(field, value) => handleFormDataChange(4, field, value)}
                />
        );
      case 4:
        return (
          <DepositSubmit
            data={allData}
            booked={booked}
            // onChange={(field, value) => handleFormDataChange(4, field, value)}
          />
        );
      default:
        return null;
    }
  };

  function convert(str) {
    var date = new Date(str),
      mnth = ("0" + (date.getMonth() + 1)).slice(-2),
      day = ("0" + date.getDate()).slice(-2);
    return [date.getFullYear(), mnth, day].join("-");
  }

  const onSubmit = async () => {
    const formData = new FormData();

    let validation;
    const validationErrors = validatePrimaryTravelerData(primaryTravelerData);
    const validationErrors2 =
      BookNowValidationInReservation(primaryTravelerData);
    if (!tour_booking_id) {
      validation = {
        ...validationErrors,
        ...validationErrors2,
      };
    } else {
      validation = {
        ...validationErrors,
      };
    }
    if (Object.keys(validation).length > 0) {
      toast.dismiss()
      toast.error("Please Check for missing/required fields.");
      setErrors(validation);
      return;
    }
    setLoading(true);
    if (tour_booking_id) {
      formData.append("tour_booking_id", tour_booking_id);
    }
    Object.entries({
      ...primaryTravelerData,
      ...(passengerNo > 1 ? secondaryTravelerData : {}),
      ...accommodationsData,
      ...travelInsuranceData,
    }).map(([key, value]) => {
      if (key == "tour_uuid") {
        formData.append("tour_uuid", id);
        return;
      }
      if (key == "date_of_birth") {
        // formData.append("date_of_birth", convert(value));
        formData.append("date_of_birth", value);
        return;
      }
      if (key == "passport_expiry_date") {
        formData.append("passport_expiry_date", value);
        return;
      }
      if (key == "secondary_passport_expiry_date") {
        if (value)
          formData.append("secondary_passport_expiry_date", value);
        return;
      }
      if (key == "secondary_date_of_birth") {
        if (value) formData.append("secondary_date_of_birth", value);
        return;
      }
      // if (key == "beds") {
      //   formData.append("beds", 1);
      //   return;
      // }
      if (key == "sharing_accommodation") {
        if(value == null) {
          formData.append(key, 0);
          return;
        }
        formData.append(key, value);
        return;
      }
      if (key == "require_smoking_room") {
        formData.append(key, value);
        return;
      }
      if (key == "datesAndCities") {
        formData.append("tour_departure_date_id", value?.id);
        formData.append("arrival_city_id", value?.arrival);
        formData.append("departure_city_id", value?.departure);
        console.log({
          tour_departure_date_id: value?.id,
          arrival_city_id: value?.arrival,
          departure_city_id: value?.departure,
        });
      } else {
        if (value) formData.append(key, value);
      }
    });
    formData.append('is_custom_form', (isCustomForm ? 1 : 0).toString());

    //  formData.append("companion", "yes");
    const response = await ApiCall.any(apiRoutes.reservation, {}, formData);
    if (response?.status === 1) {
      setBooked(true);
      toast.success("Reservation saved successfully!", { duration: 2500 });
    }
    setLoading(false);
  };

  return (
    <div style={{ backgroundColor: "" }}>
      <div
        style={{ paddingTop: 50 }}
        className="container d-flex justify-content-center align-items-center flex-column"
      >
        <h1 className="fw-bold">Traveler Profile</h1>
        {activeStep == 1 ? (
          <div className="container w-75 text-center">
            If traveling single, please skip past this secondary traveler
            section
          </div>
        ) : (
          <div className="container w-75 text-center">
            IMPORTANT: In order to avoid any unnecessary change fees, it is
            imperative that all guest names are entered correctly from the
            start. The information below must be the legal name and be 100%
            identical to the ID being used to travel. *Please provide a scanned
            copy of your Passport to Trip Merchant.
          </div>
        )}
        <div className="w-75">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            activeColor="rgba(242, 89, 38)" // Set the active color
            completeColor="rgba(242, 89, 38)" // Set the complete color
            defaultColor="#ccc" // Set the default color
            circleFontSize={20} // Adjust the circle font size
            activeTitleColor="rgba(242, 89, 38)" // Set the active title color
          />
        </div>
        <Form
          className="d-flex justify-content-between align-items-center container flex-column"
          style={{ padding: "20px" }}
        >
          {getSectionComponent()}

          <div className="d-flex justify-content-around align-items-center container mt-2">
            {activeStep !== 0 && (
              <Button
                className="bg-color-default text-white"
                onClick={() => {
                  window.scrollTo({
                    top: 70,
                    behavior: "smooth",
                  });
                  setActiveStep(activeStep - 1);
                }}
              >
                Previous
              </Button>
            )}
            {activeStep !== steps.length - 1 && (
              <Button
                className="bg-color-default text-white"
                onClick={() => {
                  if (activeStep === 0) {
                    let validation;
                    const validationErrors =
                      validatePrimaryTravelerData(primaryTravelerData);
                    const validationErrors2 =
                      BookNowValidationInReservation(primaryTravelerData);
                    if (!tour_booking_id) {
                      validation = {
                        ...validationErrors,
                        ...validationErrors2,
                      };
                    } else {
                      validation = {
                        ...validationErrors,
                      };
                    }
                    if (Object.keys(validation).length === 0) {
                      window.scrollTo({
                        top: 40,
                        behavior: "smooth",
                      });
                      setActiveStep(activeStep + 1);
                    } else {
                      toast.dismiss()
                      toast.error("Check above for missing/required fields.");
                      setErrors(validation);
                    }
                  }
                  else if((passengerNo > 1 && activeStep == 1)) {
                    let validation;
                    const validationErrors = validateSecondaryTravelerData(secondaryTravelerData);
                    validation = {
                      ...validationErrors,
                    };
                    if (Object.keys(validation).length === 0) {
                      window.scrollTo({
                        top: 40,
                        behavior: "smooth",
                      });
                      setActiveStep(activeStep + 1);
                    } else {
                      toast.dismiss()
                      toast.error("Check above for missing/required fields.");
                      setErrors(validation);
                    }
                  }
                  else if((passengerNo < 2 && activeStep == 1) || (passengerNo > 1 && activeStep == 2)) {
                    let validation;
                    const validationErrors = validateAccomodationData(accommodationsData, passengerNo)
                    validation = {
                      ...validationErrors,
                    };
                    if (Object.keys(validation).length === 0) {
                      window.scrollTo({
                        top: 40,
                        behavior: "smooth",
                      });
                      setActiveStep(activeStep + 1);
                    } else {
                      toast.dismiss()
                      toast.error("Check above for missing/required fields.");
                      setErrors(validation);
                    }
                  }
                  else if((passengerNo < 2 && activeStep == 2) || (passengerNo > 1 && activeStep == 3)) {
                    let validation;
                    const validationErrors = validateTravelInsurenceData(travelInsuranceData)
                    validation = {
                      ...validationErrors,
                    };
                    if (Object.keys(validation).length === 0) {
                      window.scrollTo({
                        top: 40,
                        behavior: "smooth",
                      });
                      setActiveStep(activeStep + 1);
                    } else {
                     toast.dismiss()
                      toast.error("Check above for missing/required fields.")
                      setErrors(validation);
                    }
                  }
                  else {
                    window.scrollTo({
                      top: 40,
                      behavior: "smooth",
                    });
                    setActiveStep(activeStep + 1);
                  }
                }}
              >
                Next
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button
                className={booked ? undefined : "bg-color-default text-white"}
                onClick={onSubmit}
                disabled={booked == true ? true : loading}
              >
                {loading ? <Loader /> : "Submit"}
              </Button>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ReservationForm;
